if (!customElements.get("wrg-gallery-component")) {
  class WRGGallery extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.selectors = {
        slideControls: ".m-slider-controls",
        slideContainer: ".m-mixed-layout__wrapper", // slideContainer
      };
      this.domNodes = queryDomNodes(this.selectors, this);

      this.enableSlider = this.dataset.enableSlider === "true";
      this.items = this.dataset.items;                // Desktop items per row
      this.itemsTablet = this.dataset.itemsTablet;    // Tablet items per row
      this.itemsMobile = this.dataset.itemsMobile;    // Mobile items per row
      this.total = this.dataset.total;
      this.autoplay = this.dataset.autoplay === "true";
      this.autoplaySpeed = this.dataset.autoplaySpeed;
      this.paginationType = this.dataset.paginationType;
      this.expanded = this.dataset.expanded === "true";
      this.mobileDisableSlider = this.dataset.mobileDisableSlider === "true";
      this.enableLightbox = this.dataset.enableLightbox === "true";

      this.initByScreenSize();
      document.addEventListener("matchMobile", () => {
        this.initByScreenSize();
      });
      document.addEventListener("unmatchMobile", () => {
        this.initByScreenSize();
      });

      // Initialize lightbox if enabled
      if (this.enableLightbox) {
        this.initLightbox();
      }

      // Initialize video players
      this.initVideoPlayers();
    }

    initByScreenSize() {
      if (!this.enableSlider) return;
      const { slideContainer, slideControls } = queryDomNodes(this.selectors, this);

      if (MinimogTheme.config.mqlMobile && this.mobileDisableSlider) {
        slideControls?.classList.add("m:hidden");
        slideContainer.classList.remove("swiper-container");
        if (this.swiper) this.swiper.destroy(false, true);
      } else {
        slideControls?.classList.remove("m:hidden");
        this.initSlider();
      }
    }

    initSlider() {
      const { slideContainer } = queryDomNodes(this.selectors, this);

      slideContainer?.classList.add("swiper-container");

      const paginationIcon = `<svg width="65px" height="65px" viewBox="0 0 72 72" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><circle class="time" stroke-width="5" fill="none" stroke-linecap="round" cx="33" cy="33" r="28"></circle></svg>`;
      this.slider = new MinimogLibs.Swiper(slideContainer, {
        slidesPerView: this.expanded && this.total > this.itemsMobile ? parseInt(this.itemsMobile) + 1 : parseInt(this.itemsMobile), // Mobile view
        loop: true,
        slidesPerGroup: 1,
        showNavigation: true,
        showPagination: true,
        parallax: true,
        autoplay: this.autoplay
          ? {
              delay: parseInt(this.autoplaySpeed) * 1000,
            }
          : false,
        pagination:
          this.paginationType == "fraction"
            ? {
                el: this.querySelector(".swiper-pagination"),
                clickable: true,
                type: "fraction",
              }
            : {
                el: this.querySelector(".swiper-pagination"),
                clickable: true,
                bulletClass: "m-dot",
                bulletActiveClass: "m-dot--active",
                renderBullet: function (index, className) {
                  return '<span class="' + className + '">' + paginationIcon + "</span>";
                },
              },
        autoHeight: true,
        breakpoints: {
          480: {
            slidesPerView: this.expanded && this.total > this.itemsTablet ? parseInt(this.itemsTablet) + 1 : parseInt(this.itemsTablet), // Tablet view
          },
          768: {
            slidesPerView: this.expanded && this.total > this.items ? parseInt(this.items) + 1 : parseInt(this.items), // Desktop view
          },
          1280: {
            slidesPerView: this.expanded && this.total > this.items ? parseInt(this.items) + 1 : parseInt(this.items), // Larger desktop view
          },
        },
        on: {
          init() {
            setTimeout(() => {
              window.dispatchEvent(new Event("resize"));
            }, 100);
          },
        },
      });

      const controlsContainer = this.querySelector(".m-slider-controls");
      const prevButton = controlsContainer && controlsContainer.querySelector(".m-slider-controls__button-prev");
      const nextButton = controlsContainer && controlsContainer.querySelector(".m-slider-controls__button-next");

      if (this.slider && prevButton && nextButton) {
        prevButton && prevButton.addEventListener("click", () => this.slider.slidePrev());
        nextButton && nextButton.addEventListener("click", () => this.slider.slideNext());
      }

      this.swiper = slideContainer?.swiper;
    }

    /**
     * Setup PhotoSwipe lightbox
     */
    handlePhotoswipe() {
      // Get only image items (not videos) and exclude duplicates
      const galleryItems = [...this.querySelectorAll('.wrg-gallery-item:not(.swiper-slide-duplicate)')];
      if (!galleryItems.length) return;

      // Build data source for PhotoSwipe
      // Only include items that have images (not videos)
      const data = [];
      galleryItems.forEach((item) => {
        const link = item.querySelector('a[data-pswp-src]');
        if (link) {
          const img = link.querySelector('img');
          const { index: id } = item.dataset;

          data.push({
            src: link.dataset.pswpSrc,
            width: parseInt(link.dataset.pswpWidth) || 1200,
            height: parseInt(link.dataset.pswpHeight) || 1200,
            alt: img ? img.alt : '',
            id,
          });
        }
      });

      const options = {
        dataSource: data,
        bgOpacity: 1,
        close: false,
        zoom: false,
        arrowNext: false,
        arrowPrev: false,
        counter: false,
        preloader: false,
        pswpModule: MinimogLibs.PhotoSwipeLightbox.PhotoSwipe,
      };
      this.lightbox = new MinimogLibs.PhotoSwipeLightbox(options);

      this.lightbox.addFilter('thumbEl', (thumbEl, { id }) => {
        const item = this.querySelector(`.wrg-gallery-item:not(.swiper-slide-duplicate)[data-index="${id}"]`);
        return item ? item.querySelector('img') : thumbEl;
      });

      this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id } }) => {
        const item = this.querySelector(`.wrg-gallery-item:not(.swiper-slide-duplicate)[data-index="${id}"]`);
        const img = item ? item.querySelector('img') : null;
        return img ? img.src : placeholderSrc;
      });

      // UI elements
      const uiElements = [
        {
          name: 'm-close',
          order: 11,
          isButton: true,
          html: '<svg class="m-svg-icon--large" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>',
          onClick: (event, el, pswp) => pswp.close(),
        },
        {
          name: 'm-arrowNext',
          order: 12,
          isButton: true,
          html: '<svg fill="currentColor" width="14px" height="14px" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M218.101 38.101L198.302 57.9c-4.686 4.686-4.686 12.284 0 16.971L353.432 230H12c-6.627 0-12 5.373-12 12v28c0 6.627 5.373 12 12 12h341.432l-155.13 155.13c-4.686 4.686-4.686 12.284 0 16.971l19.799 19.799c4.686 4.686 12.284 4.686 16.971 0l209.414-209.414c4.686-4.686 4.686-12.284 0-16.971L235.071 38.101c-4.686-4.687-12.284-4.687-16.97 0z"></path></svg>',
          onClick: (event, el, pswp) => pswp.next(),
        },
        {
          name: 'm-arrowPrev',
          order: 10,
          isButton: true,
          html: '<svg width="14px" height="14px" fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"><path d="M229.9 473.899l19.799-19.799c4.686-4.686 4.686-12.284 0-16.971L94.569 282H436c6.627 0 12-5.373 12-12v-28c0-6.627-5.373-12-12-12H94.569l155.13-155.13c4.686-4.686 4.686-12.284 0-16.971L229.9 38.101c-4.686-4.686-12.284-4.686-16.971 0L3.515 247.515c-4.686 4.686-4.686 12.284 0 16.971L212.929 473.9c4.686 4.686 12.284 4.686 16.971-.001z"></path></svg>',
          onClick: (event, el, pswp) => pswp.prev(),
        },
      ];

      this.lightbox.on('uiRegister', () => {
        this.lightbox.pswp.ui.registerElement(uiElements[0]);
        uiElements.slice(1).forEach((element) => this.lightbox.pswp.ui.registerElement(element));
      });
    }

    /**
     * Initialize PhotoSwipe lightbox and add click handlers
     */
    initLightbox() {
      // Setup PhotoSwipe
      this.handlePhotoswipe();

      // Initialize lightbox
      this.lightbox.init();

      // Add click handlers - find correct index in data array by matching id
      const galleryItems = this.querySelectorAll('.wrg-gallery-item:not(.swiper-slide-duplicate)');

      galleryItems.forEach((item) => {
        const link = item.querySelector('a[data-pswp-src]');
        if (link) {
          link.addEventListener('click', (e) => {
            e.preventDefault();
            // Find the index in the PhotoSwipe data array by matching the id
            const itemId = item.dataset.index;
            const dataIndex = this.lightbox.options.dataSource.findIndex(data => data.id === itemId);
            if (dataIndex !== -1) {
              this.lightbox.loadAndOpen(dataIndex);
            }
          });
        }
      });
    }

    /**
     * Initialize video players
     */
    initVideoPlayers() {
      const videos = this.querySelectorAll('.wrg-gallery-video');

      videos.forEach((wrapper) => {
        const video = wrapper.querySelector('video');
        const playBtn = wrapper.querySelector('.wrg-gallery-video__play-btn');

        if (!video || !playBtn) return;

        // Play button click
        playBtn.addEventListener('click', () => {
          if (video.paused) {
            video.play();
            playBtn.classList.add('is-playing');
            playBtn.setAttribute('aria-label', 'Pause video');
          } else {
            video.pause();
            playBtn.classList.remove('is-playing');
            playBtn.setAttribute('aria-label', 'Play video');
          }
        });

        // Video events
        video.addEventListener('play', () => {
          playBtn.classList.add('is-playing');
        });

        video.addEventListener('pause', () => {
          playBtn.classList.remove('is-playing');
        });

        video.addEventListener('ended', () => {
          playBtn.classList.remove('is-playing');
          video.currentTime = 0;
        });

        // Auto-hide play button when playing
        let hideTimeout;
        wrapper.addEventListener('mouseenter', () => {
          clearTimeout(hideTimeout);
          playBtn.style.opacity = '1';
        });

        wrapper.addEventListener('mouseleave', () => {
          if (!video.paused) {
            hideTimeout = setTimeout(() => {
              playBtn.style.opacity = '0';
            }, 2000);
          }
        });
      });
    }

    /**
     * Cleanup when element is removed
     */
    disconnectedCallback() {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }

      if (this.lightbox) {
        this.lightbox.destroy();
        this.lightbox = null;
      }
    }
  }

  customElements.define("wrg-gallery-component", WRGGallery);
}
